/**
 * CreatePaymentRequest Component
 * Form per creare richieste di pagamento manuali
 * Accessibile da admin, gestori eventi UISP (national, regional, territorial) e SDA
 */

import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const CreatePaymentRequest = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    event_id: searchParams.get('eventId') || '',
    association_id: searchParams.get('associationId') || '',
    amount: '',
    description: '',
    deadline: '',
    bank_account_id: ''
  });

  const [events, setEvents] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssoc, setLoadingAssoc] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [searchEvent, setSearchEvent] = useState('');
  const [searchAssociation, setSearchAssociation] = useState('');

  useEffect(() => {
    fetchEvents();
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    if (formData.event_id) {
      fetchAssociations(formData.event_id);
    } else {
      setAssociations([]);
      setFormData(prev => ({ ...prev, association_id: '' }));
    }
  }, [formData.event_id]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      // /events returns { events: [...] } without success flag
      const list = response.data?.events || response.data?.data || [];
      setEvents(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchAssociations = async (eventId) => {
    setLoadingAssoc(true);
    try {
      const response = await api.get(`/events/${eventId}/registered-associations`);
      const raw = response.data?.data;
      const list = Array.isArray(raw) ? raw
        : raw?.associations || response.data?.associations || [];
      setAssociations(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching associations:', err);
      // Se fallisce (es. utente non autorizzato), non mostriamo le associazioni
      setAssociations([]);
    } finally {
      setLoadingAssoc(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await api.get('/bank-accounts');
      const rawData = response.data?.data;
      
      // I dati arrivano dal backend come un oggetto {id: {...}, id2: {...}}
      let list = [];
      if (rawData) {
        if (Array.isArray(rawData)) {
          list = rawData;
        } else if (typeof rawData === 'object') {
          list = Object.values(rawData);
        }
      }
      
      setBankAccounts(list.filter(b => b.enabled));
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      // Validazione client-side
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('L\'importo deve essere maggiore di zero');
      }

      const requestData = {
        eventId: parseInt(formData.event_id),
        associationId: parseInt(formData.association_id),
        amount: amount,
        description: formData.description.trim() || undefined,
        paymentDeadline: formData.deadline || undefined,
        bankAccountId: formData.bank_account_id || undefined
      };

      const response = await api.post('/payments/requests', requestData);

      if (response.data.success) {
        setSuccess(true);
        setFormData({
          event_id: '',
          association_id: '',
          amount: '',
          description: '',
          deadline: '',
          bank_account_id: ''
        });

        // Reindirizza dopo 2 secondi
        setTimeout(() => {
          navigate('/admin/payments');
        }, 2000);
      }
    } catch (err) {
      console.error('Error creating payment request:', err);
      setError(
        err.response?.data?.message || 
        err.message || 
        'Errore durante la creazione della richiesta di pagamento'
      );
    } finally {
      setLoading(false);
    }
  };

  // Filtra eventi in base alla ricerca
  const filteredEvents = events.filter(event => {
    if (!searchEvent) return true;
    const searchLower = searchEvent.toLowerCase();
    const title = (event.title || event.name || '').toLowerCase();
    const date = event.event_date || event.date;
    const dateStr = date ? new Date(date).toLocaleDateString('it-IT') : '';
    return title.includes(searchLower) || dateStr.includes(searchLower);
  });

  // Filtra associazioni in base alla ricerca
  const filteredAssociations = associations.filter(assoc => {
    if (!searchAssociation) return true;
    const searchLower = searchAssociation.toLowerCase();
    return (assoc.name || '').toLowerCase().includes(searchLower);
  });

  if (!['admin', 'uisp_national', 'uisp_regional', 'uisp_territorial', 'sda_nazionale', 'sda_regional', 'sda_territorial'].includes(user?.role)) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          Non sei autorizzato ad accedere a questa sezione
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col md={{ span: 8, offset: 2 }}>
          <Card className="shadow-sm">
            <Card.Header style={{ backgroundColor: '#006e39', color: 'white' }}>
              <h4 className="mb-0">
                <i className="bi bi-plus-circle me-2"></i>
                Crea Richiesta di Pagamento
              </h4>
            </Card.Header>
            <Card.Body>
              {success && (
                <Alert variant="success">
                  <i className="bi bi-check-circle me-2"></i>
                  <strong>Richiesta creata con successo!</strong>
                  <br />
                  L'associazione riceverà un'email con i dettagli del pagamento.
                  <br />
                  <small className="text-muted">Reindirizzamento in corso...</small>
                </Alert>
              )}

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}

              <Alert variant="info">
                <i className="bi bi-info-circle me-2"></i>
                Crea una richiesta di pagamento manuale per un'associazione. 
                L'associazione riceverà un'email automatica con i dettagli e potrà procedere al pagamento 
                tramite Stripe o bonifico bancario.
              </Alert>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Evento <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="mb-2">
                    <Form.Control
                      type="text"
                      placeholder="🔍 Cerca evento per nome o data..."
                      value={searchEvent}
                      onChange={(e) => setSearchEvent(e.target.value)}
                      className="mb-2"
                    />
                    {searchEvent && (
                      <small className="text-muted">
                        {filteredEvents.length} event{filteredEvents.length !== 1 ? 'i' : 'o'} trovati
                      </small>
                    )}
                  </div>
                  <Form.Select
                    name="event_id"
                    value={formData.event_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Seleziona un evento --</option>
                    {filteredEvents.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.title || event.name}
                        {(event.event_date || event.date) ? ` - ${new Date(event.event_date || event.date).toLocaleDateString('it-IT')}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Seleziona l'evento per cui viene richiesto il pagamento
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Associazione (solo iscritte all'evento) <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="mb-2">
                    <Form.Control
                      type="text"
                      placeholder="🔍 Cerca associazione per nome..."
                      value={searchAssociation}
                      onChange={(e) => setSearchAssociation(e.target.value)}
                      className="mb-2"
                      disabled={!formData.event_id || loadingAssoc}
                    />
                    {searchAssociation && (
                      <small className="text-muted">
                        {filteredAssociations.length} associazion{filteredAssociations.length !== 1 ? 'i' : 'e'} trovate
                      </small>
                    )}
                  </div>
                  <Form.Select
                    name="association_id"
                    value={formData.association_id}
                    onChange={handleChange}
                    required
                    disabled={!formData.event_id || loadingAssoc}
                  >
                    <option value="">{loadingAssoc ? 'Caricamento...' : '-- Seleziona un\'associazione --'}</option>
                    {filteredAssociations.map(assoc => (
                      <option key={assoc.id} value={assoc.id}>
                        {assoc.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {!formData.event_id 
                      ? "Seleziona prima un evento per visualizzare le associazioni iscritte" 
                      : "Scegli tra le associazioni che hanno effettuato almeno un'iscrizione a questo evento"}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Destinatario del Pagamento (IBAN/Comitato)
                  </Form.Label>
                  <Form.Select
                    name="bank_account_id"
                    value={formData.bank_account_id}
                    onChange={handleChange}
                  >
                    <option value="">-- Utilizza conto predefinito per l'evento --</option>
                    {bankAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name} {account.iban ? `(${account.iban})` : ''} - {account.beneficiary}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Seleziona facoltativamente a quale conto bancario/comitato destinare i fondi.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Importo (€) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0.01"
                    required
                  />
                  <Form.Text className="text-muted">
                    Importo totale da pagare (calcolato manualmente)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Descrizione</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Dettagli aggiuntivi sul pagamento richiesto..."
                  />
                  <Form.Text className="text-muted">
                    Informazioni aggiuntive che verranno incluse nell'email (opzionale)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Scadenza Pagamento</Form.Label>
                  <Form.Control
                    type="date"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <Form.Text className="text-muted">
                    Data entro cui il pagamento deve essere effettuato (opzionale)
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-between">
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/admin/payments')}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Indietro
                  </Button>
                  <Button
                    type="submit"
                    style={{ backgroundColor: '#006e39', borderColor: '#006e39' }}
                    disabled={loading || success}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creazione in corso...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        Crea e Invia Richiesta
                      </>
                    )}
                  </Button>
                </div>
              </Form>

              <hr className="my-4" />

              <Alert variant="secondary" className="mb-0">
                <h6><i className="bi bi-lightbulb me-2"></i>Come funziona:</h6>
                <ol className="mb-0">
                  <li>Inserisci i dettagli della richiesta di pagamento</li>
                  <li>L'associazione riceve un'email automatica con:
                    <ul>
                      <li>Importo da pagare</li>
                      <li>Coordinate bancarie per bonifico</li>
                      <li>Link per pagamento online con Stripe</li>
                    </ul>
                  </li>
                  <li>L'associazione sceglie il metodo di pagamento preferito</li>
                  <li>In caso di bonifico, dovrà caricare la ricevuta per la verifica</li>
                </ol>
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CreatePaymentRequest;
